import { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import api from '../../src/api';
import { MagicLinkUploader } from './MagicLinkUploader';
import { SpeakerRevisionPortal } from './SpeakerRevisionPortal';

interface MagicPortalData {
  eventName: string;
  folio: string;
  instructions: string;
  expiresAt: string;
}

export function MagicLinkPortal() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [portalData, setPortalData] = useState<MagicPortalData | null>(null);

  const token = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token') || '';
  }, []);
  const mode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') || '';
  }, []);

  useEffect(() => {
    const loadPortal = async () => {
      if (mode === 'revision') {
        setLoading(false);
        return;
      }

      if (!token) {
        setError('Link inválido o expirado. Solicite uno nuevo desde el portal de seguimiento.');
        setLoading(false);
        return;
      }

      try {
        const { data } = await api.get(`/speaker/portal/${encodeURIComponent(token)}`);
        setPortalData(data);
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Link inválido o expirado. Solicite uno nuevo desde el portal de seguimiento.');
      } finally {
        setLoading(false);
      }
    };

    loadPortal();
  }, [mode, token]);

  if (mode === 'revision') {
    return <SpeakerRevisionPortal token={token} />;
  }

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-10 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#0f2744]" />
      </div>
    );
  }

  if (error || !portalData) {
    return (
      <div className="max-w-2xl mx-auto py-10">
        <Card className="border-t-4 border-t-red-500">
          <CardHeader>
            <CardTitle className="text-red-700">Acceso no disponible</CardTitle>
            <CardDescription>Portal de carga de material</CardDescription>
          </CardHeader>
          <CardContent className="text-red-700 text-sm flex items-start gap-2">
            <AlertCircle className="h-4 w-4 mt-0.5" />
            {error || 'Link inválido o expirado. Solicite uno nuevo desde el portal de seguimiento.'}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto py-8 space-y-4">
      <Card>
        <CardHeader className="bg-[#0f2744] text-white rounded-t-lg">
          <CardTitle className="text-lg">Portal de Material para Ponente</CardTitle>
          <CardDescription className="text-gray-200">{portalData.eventName}</CardDescription>
        </CardHeader>
        <CardContent className="pt-4 space-y-2 text-sm">
          <p>
            <b>Folio:</b> {portalData.folio}
          </p>
          <p>{portalData.instructions}</p>
        </CardContent>
      </Card>

      <MagicLinkUploader token={token} />
    </div>
  );
}
